/**
 * Transport policy for the GM's music player (ISS-0186).
 *
 * Pure decisions only (what Previous means, whether to advance, what a pick
 * from the track list does to the shuffle bag), so they can be tested without
 * an AudioContext. The store applies them; the engine never sees them.
 */

/** Past this many seconds in, Previous restarts the track instead. */
export const PREVIOUS_RESTART_THRESHOLD = 5;

/** Per-mood play history is capped; nobody steps back fifty tracks. */
export const HISTORY_LIMIT = 50;

export type PreviousAction =
  | { kind: 'restart' }
  | { kind: 'track'; trackId: string; history: string[] };

/**
 * What Previous does. Like a CD player: more than a few seconds in, it goes
 * back to the start of this track; otherwise to the track before it in this
 * mood's history. With nothing earlier to go to, it restarts.
 *
 * `history` ends with the current track. The returned history drops it, so a
 * second Previous keeps walking back.
 */
export function resolvePrevious(
  history: readonly string[],
  currentId: string | null,
  position: number,
): PreviousAction {
  if (position > PREVIOUS_RESTART_THRESHOLD) return { kind: 'restart' };
  if (!currentId || history.length < 2 || history[history.length - 1] !== currentId) {
    return { kind: 'restart' };
  }
  const trimmed = history.slice(0, -1);
  return { kind: 'track', trackId: trimmed[trimmed.length - 1], history: trimmed };
}

/** Record a track as played. Consecutive duplicates collapse into one. */
export function pushHistory(history: readonly string[], trackId: string): string[] {
  if (history[history.length - 1] === trackId) return [...history];
  return [...history, trackId].slice(-HISTORY_LIMIT);
}

/**
 * Whether the end of a track should move on to another. Repeat-one pins the
 * current track, and a one-track mood has nowhere to go; either way the
 * engine's loop carries on.
 */
export function shouldAdvance(repeatOne: boolean, playlistLength: number): boolean {
  return !repeatOne && playlistLength > 1;
}

/**
 * A track the GM picked from the list has had its turn this cycle, so take it
 * out of the shuffle bag rather than play it again a few tracks later.
 */
export function removeFromBag(bag: readonly string[], trackId: string): string[] {
  return bag.filter((id) => id !== trackId);
}

/** m:ss, or h:mm:ss past an hour. */
export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

/** Where a track was, and when (Date.now()), for a player whose sound is off. */
export interface MusicAnchor {
  position: number;
  at: number;
}

/**
 * Where the GM's track is now, given where it was at `anchor.at`. Used when a
 * player enables sound after the sync arrived: the track kept playing for the
 * table meanwhile. Players loop while waiting, so wrap at the track's length.
 */
export function anchoredPosition(
  anchor: MusicAnchor | null,
  paused: boolean,
  now: number,
  durationSec?: number,
): number {
  if (!anchor) return 0;
  const elapsed = paused ? 0 : Math.max(0, now - anchor.at) / 1000;
  const position = Math.max(0, anchor.position) + elapsed;
  return durationSec && durationSec > 0 ? position % durationSec : position;
}
